import React, { useState } from "react";
import useFleetStore from "../stores/useFleetStore";
import { useApi } from "../apiHelpers";
import PageLayout from '../components/ui/PageLayout';

import { DataTable } from '../components/table/DataTable';
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import DropZone from '../components/ui/DropZone';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import LogoSpinner from '../components/ui/LogoSpinner';
import { Panel, StatTile } from '../components/ui/Panel';
import StepStrip from '../components/ui/StepStrip';
const WIZARD_STEPS = [
  { key: "upload", label: "Upload" },
  { key: "review", label: "Review" },
  { key: "confirm", label: "Confirm" },
];

// The last step is done as soon as the wizard reaches it: reaching it is the update completing.
const stepState = (currentStep, num) =>
  currentStep > num || (num === 3 && currentStep === 3) ? "done" : currentStep === num ? "current" : "future";

const StepIndicator = ({ currentStep }) => (
  <StepStrip
    label="Update progress"
    variant="numbered"
    className="mb-8"
    steps={WIZARD_STEPS.map((step, i) => ({ ...step, state: stepState(currentStep, i + 1) }))}
  />
);

// Columns for the three review tables. They stay stable across renders.
const SERIAL_COLUMN = {
  id: "serial",
  header: "Serial",
  accessorFn: (r) => r.serialNumber,
  cell: ({ row }) => <span className="font-mono">{row.original.serialNumber}</span>,
};
const UPDATE_COLUMNS = [
  { ...SERIAL_COLUMN, cell: ({ row }) => <span className="font-mono text-primary">{row.original.serialNumber}</span> },
  { id: "registration", header: "New Reg", accessorFn: (r) => r.registration },
  { id: "expires", header: "New Exp", accessorFn: (r) => r.expires },
  {
    id: "current",
    header: "Current",
    accessorFn: (r) => `${r.currentReg} / ${r.currentExp}`,
    cell: ({ row }) => (
      <span className="text-on-surface-variant text-xs">
        {row.original.currentReg} / {row.original.currentExp}
      </span>
    ),
  },
];
const PLAIN_COLUMNS = [
  SERIAL_COLUMN,
  { id: "registration", header: "Registration", accessorFn: (r) => r.registration },
  { id: "expires", header: "Expires", accessorFn: (r) => r.expires },
];
const getRowKey = (_row, index) => String(index);

// Stat tile tones, from the card colours this page used.
const TILE_TONE = { primary: "accent", green: "good", yellow: "warn", red: "danger", gray: "neutral" };

const StatCard = ({ label, value, color = "gray", icon, onClick, active }) => (
  <StatTile label={label} value={value} tone={TILE_TONE[color]} icon={icon} onClick={onClick} pressed={onClick ? !!active : undefined} />
);

// Collapsible section component (controlled)
const CollapsibleSection = ({ title, count, children, isOpen, onToggle, color = "gray" }) => {
  const colors = {
    primary: "text-primary",
    green: "text-green-400",
    yellow: "text-yellow-400",
    red: "text-red-400",
    gray: "text-on-surface-variant",
  };

  return (
    <div className="mb-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-surface-container-low hover:bg-surface-container rounded transition-all"
      >
        <div className="flex items-center gap-3">
          <span className={`font-semibold ${colors[color]}`}>{title}</span>
          <span className="text-xs text-on-surface-variant bg-surface-container-high px-2 py-0.5 rounded">{count}</span>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 16 16"
          className={`text-on-surface-variant transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"/>
        </svg>
      </button>
      <Reveal show={isOpen}>
        <div className="mt-2">{children}</div>
      </Reveal>
    </div>
  );
};

// Exclamation icon for warnings
const ExclamationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" className="text-red-400">
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
    <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
  </svg>
);

const FAARegistrations = () => {
  const api = useApi();
  const drones = useFleetStore((state) => state.drones);

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Categorized data
  const [willUpdate, setWillUpdate] = useState([]);
  const [noChange, setNoChange] = useState([]);
  const [notFound, setNotFound] = useState([]);

  // Track which section is expanded
  const [expandedSection, setExpandedSection] = useState("willUpdate");

  const handleSectionClick = (section) => {
    setExpandedSection(section);
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    setFile(selectedFile);
    setError("");
    setIsProcessing(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const rows = text.split("\n").map((row) => row.trim()).filter(Boolean);
        const headers = rows[0].split(",");

        const parsedData = rows.slice(1).map((row) => {
          const values = row.split(",");
          return headers.reduce((obj, header, index) => {
            obj[header.replace(/"/g, "").trim()] = values[index]?.replace(/"/g, "").trim() || null;
            return obj;
          }, {});
        });

        // Process and categorize
        const updates = [];
        const unchanged = [];
        const missing = [];

        // Create a map of existing drones for quick lookup
        const droneMap = new Map();
        drones.forEach((d) => {
          droneMap.set(d.serialnumber, d);
        });

        parsedData.forEach((row) => {
          let serialNumber = row["Serial Number"];

          // Try nickname if serial doesn't start with F02
          if (!serialNumber?.startsWith("F02")) {
            const nickname = row["Nickname"];
            if (nickname?.startsWith("F02")) {
              serialNumber = nickname;
            } else {
              return; // Skip non-Gen2
            }
          }

          const registration = row["Registration"];
          const issued = row["Issued"] ? new Date(row["Issued"]).toISOString().split("T")[0] : null;
          const expires = row["Expires"] ? new Date(row["Expires"]).toISOString().split("T")[0] : null;

          const parsedEntry = {
            serialNumber,
            registration,
            issued,
            expires,
            shortSerial: serialNumber.slice(-7),
          };

          // Check if drone exists
          const existingDrone = droneMap.get(serialNumber);

          if (!existingDrone) {
            missing.push(parsedEntry);
          } else {
            // Compare with existing data
            const existingReg = existingDrone.faareg || "";
            const existingExp = existingDrone.regexpiration
              ? new Date(existingDrone.regexpiration).toISOString().split("T")[0]
              : null;

            if (registration !== existingReg || expires !== existingExp) {
              updates.push({
                ...parsedEntry,
                currentReg: existingReg || "(none)",
                currentExp: existingExp || "(none)",
              });
            } else {
              unchanged.push(parsedEntry);
            }
          }
        });

        setWillUpdate(updates);
        setNoChange(unchanged);
        setNotFound(missing);
        setStep(2);
      } catch (err) {
        setError("Error parsing CSV file. Please check the format.");
      } finally {
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setError("Error reading the file.");
      setIsProcessing(false);
    };
    reader.readAsText(selectedFile);
  };

  const handleUpload = async () => {
    if (willUpdate.length === 0) {
      setError("No changes to upload.");
      return;
    }

    setIsUploading(true);

    try {
      const uploadData = willUpdate.map((row) => ({
        serialNumber: row.serialNumber,
        registration: row.registration,
        issued: row.issued,
        expires: row.expires,
      }));

      await api.post(`/fleet/drone-bulk-update`, {
        body: uploadData,
        onSuccess: (result) => {
          setStep(3);
        },
      });
    } catch (err) {
      console.error("Error uploading data:", err);
      setError("An error occurred while uploading. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setFile(null);
    setWillUpdate([]);
    setNoChange([]);
    setNotFound([]);
    setError("");
  };

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-2 md:pt-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-1">FAA Registration Update</h1>
        <p className="text-on-surface-variant text-sm">
          Upload your FAA registration CSV to update drone registrations
        </p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={step} />

      <FadeIn key={step}>
      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Instructions */}
          <Panel title="How to get your FAA CSV:" level={3}>
            <ol className="m-0 text-on-surface-variant text-sm space-y-1 list-decimal list-inside">
              <li>Log into your FAA DroneZone account</li>
              <li>Go to "My Inventory" or "Registered Drones"</li>
              <li>Click "Export" or "Download CSV"</li>
              <li>Upload the downloaded file below</li>
            </ol>
          </Panel>

          {/* File Upload Zone */}
          <DropZone
            what="the FAA CSV"
            accept=".csv"
            hint="Only .csv files accepted"
            chosen={file}
            busy={isProcessing ? "Processing CSV..." : false}
            onFiles={(files) => handleFileSelect(files[0])}
          />

          <Alert tone="error" show={!!error}>{error}</Alert>
        </div>
      )}

      {/* Step 2: Review */}
      {step === 2 && (
        <div className="space-y-6">
          {/* Stats - Clickable to filter sections */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Will Update"
              value={willUpdate.length}
              color="primary"
              onClick={() => handleSectionClick("willUpdate")}
              active={expandedSection === "willUpdate"}
            />
            <StatCard
              label="No Change"
              value={noChange.length}
              color="gray"
              onClick={() => handleSectionClick("noChange")}
              active={expandedSection === "noChange"}
            />
            <StatCard
              label="Not Found"
              value={notFound.length}
              color={notFound.length > 0 ? "red" : "gray"}
              onClick={() => handleSectionClick("notFound")}
              active={expandedSection === "notFound"}
              icon={notFound.length > 0 ? <ExclamationIcon /> : null}
            />
            <StatCard label="Total Gen2" value={willUpdate.length + noChange.length + notFound.length} color="gray" />
          </div>

          {/* Will Update Section */}
          {willUpdate.length > 0 && (
            <CollapsibleSection
              title="Will Update"
              count={willUpdate.length}
              color="primary"
              isOpen={expandedSection === "willUpdate"}
              onToggle={() => setExpandedSection(expandedSection === "willUpdate" ? null : "willUpdate")}
            >
              <DataTable data={willUpdate} columns={UPDATE_COLUMNS} getRowId={getRowKey} density="compact" />
            </CollapsibleSection>
          )}

          {/* Not Found Section */}
          {notFound.length > 0 && (
            <CollapsibleSection
              title="Not Found in Fleet"
              count={notFound.length}
              color="red"
              isOpen={expandedSection === "notFound"}
              onToggle={() => setExpandedSection(expandedSection === "notFound" ? null : "notFound")}
            >
              <Alert tone="warning" variant="note" className="mb-2">
                These serial numbers don't exist in your fleet and will be skipped.
              </Alert>
              <DataTable data={notFound} columns={PLAIN_COLUMNS} getRowId={getRowKey} density="compact" />
            </CollapsibleSection>
          )}

          {/* No Change Section */}
          {noChange.length > 0 && (
            <CollapsibleSection
              title="No Change"
              count={noChange.length}
              color="gray"
              isOpen={expandedSection === "noChange"}
              onToggle={() => setExpandedSection(expandedSection === "noChange" ? null : "noChange")}
            >
              <DataTable data={noChange} columns={PLAIN_COLUMNS} getRowId={getRowKey} density="compact" />
            </CollapsibleSection>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-outline-variant">
            <button
              onClick={resetWizard}
              className={buttonClass('quiet', 'md')}
            >
              ← Start Over
            </button>
            <button
              onClick={handleUpload}
              disabled={willUpdate.length === 0 || isUploading}
              aria-busy={isUploading}
              className={buttonClass('filled', 'md')}
            >
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <LogoSpinner size="inline" />
                  Uploading...
                </span>
              ) : (
                `Update ${willUpdate.length} Drone${willUpdate.length !== 1 ? 's' : ''}`
              )}
            </button>
          </div>

          <Alert tone="error" show={!!error}>{error}</Alert>
        </div>
      )}

      {/* Step 3: Complete */}
      {step === 3 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4 shadow-[0_0_24px_rgba(34,197,94,0.4)]">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="text-green-400" viewBox="0 0 16 16">
              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Update Complete!</h2>
          <p className="text-on-surface-variant mb-6">
            Successfully updated {willUpdate.length} drone registration{willUpdate.length !== 1 ? 's' : ''}.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={resetWizard}
              className={buttonClass('tonal', 'md')}
            >
              Upload Another
            </button>
            <a
              href="/fleet"
              className={buttonClass('filled', 'md')}
            >
              View Fleet
            </a>
          </div>
        </div>
      )}
      </FadeIn>
    </PageLayout>
  );
};

export default FAARegistrations;
